import { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  setIsAnonymous,
  setPostType,
} from "features/createPost/selectPostTypeSlice";
import { chooseImage } from "features/settings/profileImageSlice";
import { toast } from "react-toastify";
import { setPost, setErrorField } from "features/posts/postSlice";
import Resizer from "react-image-file-resizer";
import SpinnerSmall from "components/SpinnerSmall";
import { IoMdClose } from "react-icons/io";
import { FaPersonPraying, FaPencil } from "react-icons/fa6";
import { useTranslation } from "react-i18next";

const POST_TYPES = [
  { key: "Post", label: "Post", Icon: FaPencil },
  { key: "Prayer Request", label: "Prayer Request", Icon: FaPersonPraying },
];

export default function CreatePostFormData() {
  const { t } = useTranslation(["feed", "prayer"]);
  const dispatch = useDispatch();
  const [createPostText, setCreatePostText] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const { postingTo } = useSelector((state) => state.postingTo);
  const { user } = useSelector((state) => state.auth);
  const { postType, isAnonymous } = useSelector((state) => state.postType);
  const { createPostPostingTo, isPostCreatedLoading, errorField } = useSelector(
    (state) => state.posts,
  );

  const handlePostTextChange = (e) => {
    if (errorField === "postText") dispatch(setErrorField(""));
    setCreatePostText(e.target.value);
  };

  const handleAnonymous = () => dispatch(setIsAnonymous(!isAnonymous));
  const handlePublic = () => setIsPublic((prev) => !prev);

  const [{ alt, src, edited, fileSrc }, setImg] = useState({
    src: null,
    alt: "",
    edited: false,
  });

  const handleImg = async (e) => {
    if (e.target.files[0]) {
      dispatch(chooseImage(e.target.files[0]));
      setImg({
        src: URL.createObjectURL(e.target.files[0]),
        alt: e.target.files[0].name,
        fileSrc: e.target.files[0],
        edited: true,
      });
    }
  };

  const removeImagePreview = () => {
    dispatch(chooseImage(null));
    setImg({ src: null, alt: "", fileSrc: "", edited: false });
  };

  const handleSubmit = async () => {
    const formData = new FormData();
    if (!createPostPostingTo) {
      toast.error(t("feed:composer.selectDestinationError"));
      return dispatch(setErrorField("postingTo"));
    }
    if (!postType) {
      toast.error(t("feed:composer.selectTypeError"));
      return dispatch(setErrorField("postType"));
    }
    if (createPostText.length < 3) {
      toast.error(t("feed:composer.tooShortError"));
      return dispatch(setErrorField("postText"));
    }

    if (createPostPostingTo._id) {
      formData.append("postedToId", createPostPostingTo._id);
      if (fileSrc) formData.append("file", fileSrc);
      formData.append("isAnonymous", isAnonymous);
      formData.append("isPublic", isPublic);
      formData.append("postType", postType);
      formData.append("postText", createPostText);
      dispatch(setPost(formData));
    } else {
      toast.error(t("feed:composer.destinationError"));
    }
  };

  return (
    <Wrapper>
      {/* ── Post type pills ── */}
      <TypeRow>
        {POST_TYPES.map(({ key, label, Icon }) => {
          const active = postType === key;
          return (
            <TypePill
              key={key}
              active={active}
              onClick={() => {
                if (errorField === "postType") dispatch(setErrorField(""));
                dispatch(setPostType(key));
              }}
            >
              <Icon style={{ fontSize: "14px" }} />
              <span>
                {t(
                  `feed:contentTypes.${key === "Post" ? "post" : "prayerRequest"}`,
                )}
              </span>
            </TypePill>
          );
        })}
        {errorField === "postType" && (
          <ErrorHint>{t("feed:composer.pickType")}</ErrorHint>
        )}
      </TypeRow>

      {/* ── Audience strip ── */}
      <AudienceStrip
        isPublic={isPublic}
        onClick={handlePublic}
        title={t("feed:composer.toggleAudience")}
      >
        {isPublic ? (
          <>
            <PublicIcon style={{ fontSize: "14px" }} />{" "}
            <span>{t("feed:composer.publicAudience")}</span>
          </>
        ) : (
          <>
            <LockIcon style={{ fontSize: "14px" }} />{" "}
            <span>{t("feed:composer.churchAudience")}</span>
          </>
        )}
        <ChevronHint>{t("feed:composer.tapToChange")}</ChevronHint>
      </AudienceStrip>

      {/* ── Text area ── */}
      <TA
        isError={errorField === "postText"}
        value={createPostText}
        onChange={handlePostTextChange}
        placeholder={
          postType === "Prayer Request"
            ? t("prayer:create.placeholder")
            : t("feed:composer.postPrompt")
        }
      />

      {/* ── Image preview ── */}
      {edited && (
        <ImagePreview>
          <img src={src} alt={alt} />
          <RemoveBtn onClick={removeImagePreview}>
            <IoMdClose />
          </RemoveBtn>
        </ImagePreview>
      )}

      {/* ── Toolbar ── */}
      <Toolbar>
        <ToolbarLeft>
          <ToolBtn
            as="label"
            htmlFor="imgUpload"
            title={t("feed:composer.photo")}
          >
            <AddPhotoAlternateIcon
              style={{
                fontSize: "20px",
                color: edited ? "var(--themeColor)" : undefined,
              }}
            />
            <span>
              {edited
                ? t("feed:composer.changePhoto")
                : t("feed:composer.photo")}
            </span>
            <input
              hidden
              id="imgUpload"
              type="file"
              accept=".png,.jpg,.jpeg"
              onChange={handleImg}
            />
          </ToolBtn>

          <ToolBtn
            onClick={handleAnonymous}
            active={isAnonymous}
            title={t("feed:composer.toggleAnonymous")}
          >
            <VisibilityOffIcon style={{ fontSize: "20px" }} />
            <span>{t("feed:anonymous")}</span>
          </ToolBtn>

          <ToolBtn
            onClick={handlePublic}
            active={isPublic}
            title={isPublic ? "Public" : "Church only"}
          >
            {isPublic ? (
              <PublicIcon style={{ fontSize: "20px" }} />
            ) : (
              <LockIcon style={{ fontSize: "20px" }} />
            )}
            <span>
              {isPublic ? t("feed:composer.public") : t("feed:composer.church")}
            </span>
          </ToolBtn>
        </ToolbarLeft>

        <PostBtn onClick={handleSubmit} disabled={isPostCreatedLoading}>
          {isPostCreatedLoading ? <SpinnerSmall /> : t("feed:composer.submit")}
        </PostBtn>
      </Toolbar>
    </Wrapper>
  );
}

/* ── Styled components ─────────────────────────────────────────────────────── */

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  border-top: 1px solid #f0f0f0;
`;

const TypeRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 12px 20px 8px;
`;

const TypePill = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 20px;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: ${(p) => (p.active ? 700 : 500)};
  background-color: ${(p) => (p.active ? "var(--themeColor)" : "#f0f0f0")};
  color: ${(p) => (p.active ? "#fff" : "var(--lightThemeFontColor)")};
  transition:
    background 0.15s,
    color 0.15s;

  &:hover {
    background-color: ${(p) => (p.active ? "var(--themeColor)" : "#e4e4e4")};
  }
`;

const ErrorHint = styled.span`
  font-size: 11px;
  color: #f44336;
  margin-left: 4px;
`;

const AudienceStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 20px;
  background-color: ${(p) => (p.isPublic ? "rgba(0,180,204,0.07)" : "#fafafa")};
  border-top: 1px solid #f0f0f0;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  color: ${(p) => (p.isPublic ? "var(--themeColor)" : "var(--lightThemeFontColor)")};
  font-size: 12px;
  transition: background 0.15s;

  svg {
    flex-shrink: 0;
  }

  &:hover {
    background-color: ${(p) => (p.isPublic ? "rgba(0,180,204,0.12)" : "#f3f3f3")};
  }
`;

const ChevronHint = styled.span`
  margin-left: auto;
  font-size: 11px;
  opacity: 0.5;
`;

const TA = styled.textarea`
  resize: none;
  width: 100%;
  min-height: 130px;
  border: none;
  outline: none;
  padding: 16px 20px;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 15px;
  color: #333;
  background: transparent;
  border: ${(p) => (p.isError ? "1px solid #f44336" : "none")};

  &::placeholder {
    font-size: 14px;
    color: #aaa;
  }
`;

const ImagePreview = styled.div`
  position: relative;
  margin: 0 20px 10px;
  border-radius: 10px;
  overflow: hidden;
  max-height: 280px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const RemoveBtn = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  &:hover {
    background: rgba(0, 0, 0, 0.65);
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px 6px;
  border-top: 1px solid #f0f0f0;
  gap: 6px;
`;

const ToolbarLeft = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
`;

const ToolBtn = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  padding: 7px 11px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  background-color: ${(p) => (p.active ? "rgba(0,180,204,0.12)" : "#f2f2f2")};
  color: ${(p) => (p.active ? "var(--themeColor)" : "var(--lightThemeFontColor)")};
  transition: background 0.15s;
  border: none;

  &:hover {
    background-color: ${(p) => (p.active ? "rgba(0,180,204,0.18)" : "#e8e8e8")};
  }

  @media (max-width: 480px) {
    padding: 6px 8px;
    span {
      display: none;
    }
  }
`;

const PostBtn = styled.button`
  padding: 9px 24px;
  border-radius: 20px;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  background-color: var(--themeColor);
  color: #fff;
  min-width: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.88;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;
